import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { YmsAlertDialog } from '@/components/dashboard/YmsAlertDialog';
import { InactivityWarningDialog } from '@/components/InactivityWarningDialog';
import { Authentticatevalues, ROUTES, IMAGE_CONSTANTS } from '@/constants';
import { usePreventBackNavigation } from '@/hooks/usePreventBackNavigation';
import { useSessionManager } from '@/hooks/useSessionManager';
import { UpdateUserLogAsync } from '@/service/AuthenticationService';
import { useAppSelector, selectUser, selectPlant, selectSessionId } from '@/store';

export function WorkModeSelectionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useAppSelector(selectUser);
  const plant = useAppSelector(selectPlant);
  const sessionId = useAppSelector(selectSessionId);

  const { showWarning, countdown, onContinue } = useSessionManager();
  const [showExitDialog, setShowExitDialog] = useState(false);
  const allowNavigationRef = useRef(false);

  const handleBackAttempt = useCallback(() => {
    setShowExitDialog(true);
  }, []);

  usePreventBackNavigation(true, allowNavigationRef, handleBackAttempt);

  const handleYardWorkClick = useCallback(async () => {
    try {
      await UpdateUserLogAsync({
        user: user?._id ?? '',
        status: Authentticatevalues.UserStatusStartYardWork,
        activity_date_time: new Date().toISOString(),
        session_id: sessionId ?? '',
        plant_id: plant?._id,
      });
    } catch {
      // Fire-and-forget
    }
    navigate(ROUTES.dashboard);
  }, [navigate, user?._id, sessionId, plant?._id]);

  const handleShuttleTransferClick = useCallback(async () => {
    try {
      await UpdateUserLogAsync({
        user: user?._id ?? '',
        status: Authentticatevalues.UserStatusEndYardWork,
        activity_date_time: new Date().toISOString(),
        session_id: sessionId ?? '',
        plant_id: plant?._id,
      });
    } catch {
      // Fire-and-forget
    }
    navigate(ROUTES.shuttleTransfer);
  }, [navigate, user?._id, sessionId, plant?._id]);

  return (
    <div className="flex flex-col px-6 pt-4 pb-6">
      {/* Work Type Icon */}

      <img
        src={IMAGE_CONSTANTS.icWorkType}
        alt=""
        className="h-[35px] w-[35px] mb-2"
        aria-hidden="true"
      />

      {/* Title */}

      <h1 className="font-poppins text-[18px] font-semibold text-[#243743]">
        {t('workMode.title', 'Please Select Your Work Mode')}
      </h1>

      {/* Subtitle */}

      <p className="font-poppins text-[14px] font-medium text-[#516573] mt-1">
        {t(
          'workMode.subtitle',

          'Choose your work mode. You can switch modes anytime within the app.'
        )}
      </p>

      {/* Work Mode Cards */}

      <div className="flex flex-col items-center gap-4 mt-6 md:flex-row md:justify-center md:gap-16">
        {/* Start Yard Work Card */}

        <button
          onClick={() => void handleYardWorkClick()}
          className="flex flex-col items-center justify-end px-3 pb-4 w-full max-w-[240px] h-[160px] md:max-w-[260px] md:h-[220px] rounded-lg border border-gray-200 bg-white shadow-sm hover:border-[#1457A0] hover:shadow-md transition-all cursor-pointer md:pb-6"
        >
          <img
            src={IMAGE_CONSTANTS.icWorkTypeYard}
            alt={t('workMode.yardWork')}
            className="h-[60px] w-[60px] mb-4 md:h-[90px] md:w-[90px] md:mb-6"
          />

          <span
            className="font-poppins text-[15px] md:text-[18px] text-[#516573] text-center whitespace-nowrap text-wrap"
            style={{ fontWeight: 600 }}
          >
            {t('workMode.yardWork', 'Start Yard Work')}
          </span>
        </button>

        {/* Start Shuttle / Transfer Card — only visible to allowed roles */}

        <button
          onClick={() => void handleShuttleTransferClick()}
          className="flex flex-col items-center justify-end px-3 pb-4 w-full max-w-[240px] h-[160px] md:max-w-[260px] md:h-[220px] rounded-lg border border-gray-200 bg-white shadow-sm hover:border-[#1457A0] hover:shadow-md transition-all cursor-pointer md:pb-6"
        >
          <img
            src={IMAGE_CONSTANTS.icWorkTypeShuttle}
            alt={t('workMode.shuttleTransfer')}
            className="h-[60px] w-[60px] mb-4 md:h-[90px] md:w-[90px] md:mb-6"
          />

          <span
            className="font-poppins text-[15px] md:text-[18px] text-[#516573] text-center whitespace-nowrap text-wrap"
            style={{ fontWeight: 600 }}
          >
            {t('workMode.shuttleTransfer', 'Start Shuttle / Transfer')}
          </span>
        </button>
      </div>

      <YmsAlertDialog
        open={showExitDialog}
        title={t('common.exitConfirmTitle', 'Exit Application')}
        message={t('common.exitConfirmMessage', 'Are you sure you want to exit the application?')}
        okText={t('common.yes', 'Yes')}
        cancelText={t('common.no', 'No')}
        showCancel
        onOk={() => {
          allowNavigationRef.current = true;
          setShowExitDialog(false);
          navigate(ROUTES.logoutSummary);
        }}
        onCancel={() => {
          setShowExitDialog(false);
        }}
      />

      <InactivityWarningDialog open={showWarning} countdown={countdown} onContinue={onContinue} />
    </div>
  );
}
